import React, { useState, useEffect } from 'react';
import { BackofficeLayout } from '../../layouts/BackofficeLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Plus, FileText, Trash2, Search } from 'lucide-react';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';

const EMPTY = { name: '', channel: 'email', subject: '', body: '', variables: '', tone: 'plainspoken_trade' };

const CHANNEL_COLORS = {
  email: 'text-blue-400 border-blue-500/30',
  linkedin: 'text-sky-400 border-sky-500/30',
  call_script: 'text-amber-400 border-amber-500/30',
  landing_copy: 'text-green-400 border-green-500/30',
};

export default function BackofficeTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = (q = '') => {
    setLoading(true);
    api.get('/backoffice/templates', { params: { search: q || undefined } })
      .then(r => setTemplates(r.data.templates || []))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    try {
      const payload = { ...form, variables: form.variables ? form.variables.split(',').map(v => v.trim()) : [] };
      if (editId) {
        await api.put(`/backoffice/templates/${editId}`, payload);
        toast.success('Template updated');
      } else {
        await api.post('/backoffice/templates', payload);
        toast.success('Template created');
      }
      setShowForm(false);
      setForm(EMPTY);
      setEditId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const openEdit = (t) => {
    setEditId(t.id);
    setForm({ name: t.name, channel: t.channel, subject: t.subject || '', body: t.body, variables: (t.variables || []).join(', '), tone: t.tone });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    await api.delete(`/backoffice/templates/${id}`);
    toast.success('Deleted');
    load();
  };

  return (
    <BackofficeLayout>
      <div data-testid="backoffice-templates">
        <div className="flex items-center justify-between mb-5">
          <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>TEMPLATES</h1>
          <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" onClick={() => { setEditId(null); setForm(EMPTY); setShowForm(true); }} data-testid="create-template-btn">
            <Plus className="w-4 h-4 mr-1" /> New
          </Button>
        </div>

        <div className="relative mb-4">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-500" />
          <Input
            placeholder="Search templates..."
            className="pl-9 bg-slate-900 border-slate-700 text-slate-200 h-9 rounded-sm"
            value={search}
            onChange={e => { setSearch(e.target.value); load(e.target.value); }}
            data-testid="template-search"
          />
        </div>

        <div className="space-y-2">
          {loading ? (
            [...Array(3)].map((_, i) => <div key={i} className="h-16 bg-slate-900 border border-slate-800 rounded-sm animate-pulse" />)
          ) : templates.length === 0 ? (
            <p className="text-slate-500 text-sm py-8 text-center">No templates yet</p>
          ) : (
            templates.map(t => (
              <Card key={t.id} className="bg-slate-900 border-slate-800 hover:border-slate-600 transition-colors cursor-pointer" onClick={() => openEdit(t)}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-slate-600" strokeWidth={1.5} />
                    <div>
                      <p className="text-sm font-medium text-slate-200">{t.name}</p>
                      <p className="text-xs text-slate-500 truncate max-w-xs">{t.subject || t.body?.substring(0, 60)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`text-[10px] ${CHANNEL_COLORS[t.channel] || 'text-slate-400 border-slate-600'}`}>{t.channel}</Badge>
                    <Button variant="ghost" size="sm" className="text-slate-500 hover:text-red-400 h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); handleDelete(t.id); }} data-testid={`delete-tmpl-${t.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        <Dialog open={showForm} onOpenChange={(v) => { if (!v) { setShowForm(false); setEditId(null); } }}>
          <DialogContent className="bg-slate-900 border-slate-700 text-slate-200 max-w-md">
            <DialogHeader><DialogTitle className="text-white">{editId ? 'Edit' : 'New'} Template</DialogTitle></DialogHeader>
            <div className="grid gap-3">
              <div><Label className="text-slate-400 text-xs">Name *</Label><Input className="bg-slate-800 border-slate-700 text-slate-200 h-8 rounded-sm mt-1" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="tmpl-name-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-400 text-xs">Channel</Label>
                  <select className="w-full mt-1 h-8 bg-slate-800 border border-slate-700 text-slate-200 rounded-sm text-sm px-2" value={form.channel} onChange={e => setForm({...form, channel: e.target.value})}>
                    <option value="email">Email</option>
                    <option value="linkedin">LinkedIn</option>
                    <option value="call_script">Call Script</option>
                    <option value="landing_copy">Landing Copy</option>
                  </select>
                </div>
                <div><Label className="text-slate-400 text-xs">Tone</Label><Input className="bg-slate-800 border-slate-700 text-slate-200 h-8 rounded-sm mt-1" value={form.tone} onChange={e => setForm({...form, tone: e.target.value})} /></div>
              </div>
              <div><Label className="text-slate-400 text-xs">Subject</Label><Input className="bg-slate-800 border-slate-700 text-slate-200 h-8 rounded-sm mt-1" value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} /></div>
              <div><Label className="text-slate-400 text-xs">Body *</Label><Textarea className="bg-slate-800 border-slate-700 text-slate-200 rounded-sm mt-1 min-h-[120px] font-mono text-xs" value={form.body} onChange={e => setForm({...form, body: e.target.value})} data-testid="tmpl-body-input" /></div>
              <div><Label className="text-slate-400 text-xs">Variables (comma-separated)</Label><Input className="bg-slate-800 border-slate-700 text-slate-200 h-8 rounded-sm mt-1" placeholder="contact_name, company_name" value={form.variables} onChange={e => setForm({...form, variables: e.target.value})} /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" className="text-slate-400" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</Button>
              <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" onClick={handleSave} disabled={!form.name || !form.body} data-testid="save-template-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </BackofficeLayout>
  );
}
